import { createRoute, Link } from '@tanstack/react-router'
import { Button, Callout, EntrySheetTitle, MutedText, Stack } from '@volee/ui'

import { ResetConfirmForm, ResetRequestForm } from '../components/password-reset-form.tsx'
import { usePasswordReset } from '../data/use-password-reset.ts'
import { interpolate, messages } from '../messages/index.ts'
import { parseReturnTo, signInLink } from './sign-in.tsx'
import { entryRoute } from './entry.tsx'
import { NAV_PATHS } from '../navigation/paths.ts'

// Password forgotten, in re-enterable steps — which is why this one IS a route while the
// first access is a state of /sign-in. The email never enters the URL.

// A code and not a link: ours, six digits, that also reaches the tutori under 14 (spec #278, #296).
// Two steps, then, and the text says code.

export interface ForgotPasswordSearch {
  readonly returnTo: string
}

export const forgotPasswordRoute = createRoute({
  getParentRoute: () => entryRoute,
  path: NAV_PATHS.forgotPassword,
  validateSearch: (search: Record<string, unknown>): ForgotPasswordSearch => ({
    returnTo: parseReturnTo(search.returnTo),
  }),
  component: ForgotPassword,
})

/** The steps name themselves, so the card does not have to read the flags inline. */
function headingFor(step: { codeAsked: boolean; changed: boolean; toldFor: string | null }) {
  if (step.toldFor !== null) {
    return {
      title: messages.passwordReset.toldTitle,
      description: interpolate(messages.passwordReset.toldDescription, { username: step.toldFor }),
    }
  }
  if (step.changed) {
    return {
      title: messages.passwordReset.done,
      description: messages.passwordReset.signInYourself,
    }
  }
  if (step.codeAsked) {
    return {
      title: messages.passwordReset.codeTitle,
      description: messages.passwordReset.codeDescription,
    }
  }
  return { title: messages.passwordReset.title, description: messages.passwordReset.description }
}

/** A username's recovery: what the child can still do, and the staff when no tutore has an account.
 *  Said the same whether the username exists or not. */
function GuardiansTold() {
  const ui = messages.passwordReset
  return (
    <Stack gap="md">
      <Callout title={ui.forgotUsernameTitle}>{ui.forgotUsernameBody}</Callout>
      <MutedText>{ui.noGuardian}</MutedText>
    </Stack>
  )
}

function ForgotPassword() {
  const { returnTo } = forgotPasswordRoute.useSearch()
  const { codeFor, request, confirm, changed, toldFor } = usePasswordReset(returnTo)

  function body() {
    if (toldFor !== null) return <GuardiansTold />
    if (codeFor === null) return <ResetRequestForm {...request} />
    // Reached only when the sign-in that follows failed: on success this page is already gone. The title
    // and its line above have said it all.
    if (changed) return null
    return <ResetConfirmForm {...confirm} />
  }

  // The step owns its own heading: the two forms used to carry a second copy of it.
  const heading = headingFor({ codeAsked: codeFor !== null, changed, toldFor })

  return (
    <Stack gap="md">
      <Stack gap="sm">
        <EntrySheetTitle>{heading.title}</EntrySheetTitle>
        <MutedText>{heading.description}</MutedText>
      </Stack>
      {body()}
      <Button variant="ghost" asChild>
        <Link {...signInLink(returnTo)}>{messages.passwordReset.backToSignIn}</Link>
      </Button>
    </Stack>
  )
}
